/**
 * useDownloadAllAsZip - Hook reutilizável para baixar múltiplos arquivos como ZIP
 * Inclui componente DownloadAllZipButton para uso em loops (onde hooks não podem ser chamados)
 * 
 * Faz fetch de cada arquivo, compacta via JSZip e dispara download no browser.
 * Trata nomes duplicados adicionando sufixo numérico.
 * 
 * @version 1.1.0
 * RELEASE: 2026-04-14 v1.0.0 - Extração de lógica do TaskDescriptionSection
 * RELEASE: 2026-04-14 v1.1.0 - Adicionado componente DownloadAllZipButton
 */
import React, { useState, useCallback } from 'react';
import JSZip from 'jszip';
import { Download, Loader2 } from 'lucide-react';

/** Interface mínima de anexo necessária para o download */
export interface DownloadableAttachment {
  file_name: string;
  public_url: string;
}

/**
 * Hook para compactar e baixar múltiplos anexos como arquivo ZIP
 * @param attachments - Lista de anexos com file_name e public_url
 * @param zipFileName - Nome do arquivo ZIP gerado (default: 'anexos.zip')
 */
export function useDownloadAllAsZip(
  attachments: DownloadableAttachment[] | undefined,
  zipFileName = 'anexos.zip'
) {
  const [isZipping, setIsZipping] = useState(false);

  /**
   * Baixa todos os anexos, compacta em ZIP e dispara download
   */
  const handleDownloadAll = useCallback(async () => {
    if (!attachments || attachments.length === 0) return;
    setIsZipping(true);
    try {
      const zip = new JSZip();

      // Faz fetch de todos os arquivos em paralelo
      const results = await Promise.all(
        attachments.map(async (att) => {
          const res = await fetch(att.public_url);
          const blob = await res.blob();
          return { name: att.file_name, blob };
        })
      );

      // Adiciona ao ZIP tratando nomes duplicados
      const usedNames = new Map<string, number>();
      results.forEach(({ name, blob }) => {
        let finalName = name;
        const count = usedNames.get(name) || 0;
        if (count > 0) {
          const dotIdx = name.lastIndexOf('.');
          const base = dotIdx > 0 ? name.substring(0, dotIdx) : name;
          const ext = dotIdx > 0 ? name.substring(dotIdx) : '';
          finalName = `${base}_${count}${ext}`;
        }
        usedNames.set(name, count + 1);
        zip.file(finalName, blob);
      });

      // Gera blob do ZIP e dispara download
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = zipFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      // Erro silencioso — evita quebrar UX
    } finally {
      setIsZipping(false);
    }
  }, [attachments, zipFileName]);

  return { handleDownloadAll, isZipping };
}

/**
 * Componente botão "Baixar todos os arquivos..." 
 * Para uso em loops onde hooks não podem ser chamados diretamente.
 * Renderiza apenas quando há mais de 1 anexo.
 */
export function DownloadAllZipButton({
  attachments,
  zipFileName = 'anexos.zip',
}: {
  attachments: DownloadableAttachment[];
  zipFileName?: string;
}) {
  const { handleDownloadAll, isZipping } = useDownloadAllAsZip(attachments, zipFileName);

  if (attachments.length <= 1) return null;

  return (
    <button
      type="button"
      onClick={handleDownloadAll}
      disabled={isZipping}
      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
    >
      {isZipping ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <Download className="h-3 w-3" />
      )}
      <span>Baixar todos os arquivos...</span>
    </button>
  );
}
